// =====================================================
// REJECTION TYPES ADMIN PAGE - Con import/export Excel
// =====================================================

import { useState, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import {
  XOctagon,
  Plus,
  Search,
  Filter,
  RefreshCw,
  Edit2,
  Power,
  Loader2,
  Save,
  X,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  FileSpreadsheet,
  MessageSquare,
  MoreHorizontal,
  Building2,
} from 'lucide-react';
import { rejectionTypesApi } from '../../api/rejection-types';
import { rejectionTypesImportExport } from '../../api/import-export';
import { Modal, ModalFooter } from '../../components/ui/Modal';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import { ImportExportModal } from '../../components/ui/ImportExportModal';
import { toast } from '../../components/ui/Toast';
import type { RejectionType, RejectionTypeFormData, CreateRejectionTypeRequest, UpdateRejectionTypeRequest } from '../../types/catalog-admin';
import type { PaginatedResponse } from '../../types/pagination';

type SortField = 'name' | 'code' | 'sortOrder' | 'createdAt';

export function RejectionTypesAdminPage() {
  const [data, setData] = useState<PaginatedResponse<RejectionType> | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [showFilters, setShowFilters] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<SortField>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<RejectionType | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const [isImportExportOpen, setIsImportExportOpen] = useState(false);

  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    item: RejectionType | null;
    action: 'activate' | 'deactivate';
  }>({ isOpen: false, item: null, action: 'deactivate' });
  const [isProcessing, setIsProcessing] = useState(false);

  // Estado para el modal de asignación masiva (todos los tipos de un centro)
  const [bulkModal, setBulkModal] = useState<{
    isOpen: boolean;
    sites: { id: string; code: string; name: string }[];
    selectedSiteId: string;
    allTypes: RejectionType[];
    assignedTypeIds: string[];
    selectedTypeIds: string[];
    isLoadingSites: boolean;
    isLoadingTypes: boolean;
    isSaving: boolean;
  }>({
    isOpen: false, sites: [], selectedSiteId: '', allTypes: [],
    assignedTypeIds: [], selectedTypeIds: [],
    isLoadingSites: false, isLoadingTypes: false, isSaving: false,
  });

  // Estado para el modal de asignación de centros
  const [assignmentModal, setAssignmentModal] = useState<{
    isOpen: boolean;
    item: RejectionType | null;
    sites: { id: string; code: string; name: string; isAssigned: boolean }[];
    selectedIds: string[];
    isLoading: boolean;
    isSaving: boolean;
  }>({ isOpen: false, item: null, sites: [], selectedIds: [], isLoading: false, isSaving: false });

  const { register, handleSubmit, reset, formState: { errors } } = useForm<RejectionTypeFormData>({
    defaultValues: { code: '', name: '', requiresComment: false, isOther: false, sortOrder: 0 },
  });

  const loadData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await rejectionTypesApi.getAllPaginated({
        page, limit, search: searchQuery || undefined, sortBy, sortOrder, includeInactive: showInactive,
      });
      setData(result);
    } catch (err: any) {
      setError(err.message || 'Error al cargar tipos de rechazo');
    } finally {
      setIsLoading(false);
    }
  }, [page, limit, searchQuery, sortBy, sortOrder, showInactive]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSearch = (e: React.FormEvent) => { e.preventDefault(); setPage(1); };

  const handleCreate = () => {
    setEditingItem(null);
    setFormError(null);
    reset({ code: '', name: '', requiresComment: false, isOther: false, sortOrder: 0 });
    setIsModalOpen(true);
  };

  const handleEdit = (item: RejectionType) => {
    setEditingItem(item);
    setFormError(null);
    reset({ code: item.code, name: item.name, requiresComment: item.requiresComment, isOther: item.isOther, sortOrder: item.sortOrder });
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    if (!isSaving) {
      setIsModalOpen(false);
      setEditingItem(null);
      setFormError(null);
    }
  };

  const handleSave = async (formData: RejectionTypeFormData) => {
    // Limpiar error previo
    setFormError(null);

    // Validar código duplicado (global)
    const codeUpper = formData.code.toUpperCase().trim();
    const existingItem = data?.data.find(
      (item) => item.code.toUpperCase() === codeUpper && item.id !== editingItem?.id
    );

    if (existingItem) {
      setFormError(`Ya existe un tipo de rechazo con el código "${codeUpper}"`);
      return;
    }

    setIsSaving(true);
    try {
      if (editingItem) {
        const updateData: UpdateRejectionTypeRequest = {
          code: formData.code, name: formData.name,
          requiresComment: formData.requiresComment,
          isOther: formData.isOther, sortOrder: formData.sortOrder,
        };
        await rejectionTypesApi.update(editingItem.id, updateData);
        toast.success('Actualizado', `Tipo de rechazo "${formData.name}" actualizado`);
      } else {
        const createData: CreateRejectionTypeRequest = {
          code: formData.code, name: formData.name,
          requiresComment: formData.requiresComment,
          isOther: formData.isOther, sortOrder: formData.sortOrder,
        };
        await rejectionTypesApi.create(createData);
        toast.success('Creado', `Tipo de rechazo "${formData.name}" creado`);
      }
      setIsModalOpen(false);
      setEditingItem(null);
      setFormError(null);
      loadData();
    } catch (err: any) {
      const codeUpper = formData.code.toUpperCase().trim();
      setFormError(`Ya existe un tipo de rechazo con el código "${codeUpper}"`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleStatus = (item: RejectionType) => {
    setConfirmDialog({ isOpen: true, item, action: item.isActive ? 'deactivate' : 'activate' });
  };

  const handleConfirmToggle = async () => {
    if (!confirmDialog.item) return;
    setIsProcessing(true);
    try {
      if (confirmDialog.action === 'deactivate') {
        await rejectionTypesApi.deactivate(confirmDialog.item.id);
        toast.success('Desactivado', `Tipo de rechazo "${confirmDialog.item.name}" desactivado`);
      } else {
        await rejectionTypesApi.reactivate(confirmDialog.item.id);
        toast.success('Activado', `Tipo de rechazo "${confirmDialog.item.name}" activado`);
      }
      loadData();
    } catch (err: any) {
      toast.error('Error', err.message || 'No se pudo cambiar el estado');
    } finally {
      setIsProcessing(false);
      setConfirmDialog({ isOpen: false, item: null, action: 'deactivate' });
    }
  };

  // ── Gestión de centros asignados ────────────────────────────────────────────
  const handleOpenAssignment = async (item: RejectionType) => {
    setAssignmentModal({ isOpen: true, item, sites: [], selectedIds: [], isLoading: true, isSaving: false });
    try {
      const sites = await rejectionTypesApi.getSiteAssignments(item.id);
      setAssignmentModal(prev => ({
        ...prev,
        sites,
        selectedIds: sites.filter(s => s.isAssigned).map(s => s.id),
        isLoading: false,
      }));
    } catch {
      setAssignmentModal(prev => ({ ...prev, isLoading: false }));
      toast.error('Error', 'No se pudieron cargar los centros');
    }
  };

  const handleToggleSite = (siteId: string) => {
    setAssignmentModal(prev => ({
      ...prev,
      selectedIds: prev.selectedIds.includes(siteId)
        ? prev.selectedIds.filter(id => id !== siteId)
        : [...prev.selectedIds, siteId],
    }));
  };

  const handleSaveAssignment = async () => {
    if (!assignmentModal.item) return;
    setAssignmentModal(prev => ({ ...prev, isSaving: true }));
    try {
      await rejectionTypesApi.updateSiteAssignments(assignmentModal.item.id, assignmentModal.selectedIds);
      toast.success('Guardado', 'Asignación de centros actualizada');
      setAssignmentModal({ isOpen: false, item: null, sites: [], selectedIds: [], isLoading: false, isSaving: false });
      loadData();
    } catch {
      toast.error('Error', 'No se pudo guardar la asignación');
      setAssignmentModal(prev => ({ ...prev, isSaving: false }));
    }
  };

  // ── Asignación masiva por centro ─────────────────────────────────────────────
  const handleOpenBulkModal = async () => {
    setBulkModal(prev => ({ ...prev, isOpen: true, isLoadingSites: true }));
    try {
      const token = localStorage.getItem('imi_access_token') || '';
      const r = await fetch('/api/v1/sites/select', { headers: { Authorization: 'Bearer ' + token } });
      const sites = r.ok ? await r.json() : [];
      const allTypes = await rejectionTypesApi.getAll(false);
      setBulkModal(prev => ({ ...prev, sites, allTypes, isLoadingSites: false, selectedSiteId: sites[0]?.id || '' }));
      if (sites[0]?.id) {
        const assignedTypeIds = await rejectionTypesApi.getAssignedTypeIds(sites[0].id);
        setBulkModal(prev => ({ ...prev, assignedTypeIds, selectedTypeIds: assignedTypeIds }));
      }
    } catch {
      toast.error('Error', 'No se pudieron cargar los datos');
      setBulkModal(prev => ({ ...prev, isLoadingSites: false, isOpen: false }));
    }
  };

  const handleBulkSiteChange = async (siteId: string) => {
    setBulkModal(prev => ({ ...prev, selectedSiteId: siteId, isLoadingTypes: true }));
    try {
      const assignedTypeIds = await rejectionTypesApi.getAssignedTypeIds(siteId);
      setBulkModal(prev => ({ ...prev, assignedTypeIds, selectedTypeIds: assignedTypeIds, isLoadingTypes: false }));
    } catch {
      setBulkModal(prev => ({ ...prev, isLoadingTypes: false }));
    }
  };

  const handleBulkToggleType = (typeId: string) => {
    setBulkModal(prev => ({
      ...prev,
      selectedTypeIds: prev.selectedTypeIds.includes(typeId)
        ? prev.selectedTypeIds.filter(id => id !== typeId)
        : [...prev.selectedTypeIds, typeId],
    }));
  };

  const handleBulkSelectAll = () => {
    setBulkModal(prev => ({
      ...prev,
      selectedTypeIds: prev.selectedTypeIds.length === prev.allTypes.length
        ? [] : prev.allTypes.map(t => t.id),
    }));
  };

  const handleBulkSave = async () => {
    if (!bulkModal.selectedSiteId) return;
    setBulkModal(prev => ({ ...prev, isSaving: true }));
    try {
      await rejectionTypesApi.bulkAssignToSite(bulkModal.selectedSiteId, bulkModal.selectedTypeIds);
      toast.success('Guardado', 'Asignación masiva actualizada correctamente');
      setBulkModal({ isOpen: false, sites: [], selectedSiteId: '', allTypes: [], assignedTypeIds: [], selectedTypeIds: [], isLoadingSites: false, isLoadingTypes: false, isSaving: false });
      loadData();
    } catch {
      toast.error('Error', 'No se pudo guardar la asignación');
      setBulkModal(prev => ({ ...prev, isSaving: false }));
    }
  };

  const items = data?.data || [];
  const meta = data?.meta;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Tipos de Rechazo</h1>
          <p className="text-sm text-gray-500 mt-1">Motivos de rechazo para presupuestos</p>
        </div>
      </div>

      {/* Toolbar - Nuevo diseño */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 space-y-4">
        {/* Fila superior: Búsqueda y acciones principales */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          {/* Búsqueda y botón filtros */}
          <div className="flex items-center flex-1 max-w-2xl gap-2">
            <form onSubmit={handleSearch} className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Buscar por código o nombre..."
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg bg-gray-50 text-sm focus:bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              />
            </form>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-2 border rounded-lg text-sm font-medium transition-colors ${
                showFilters 
                  ? 'bg-blue-50 border-blue-200 text-blue-600' 
                  : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
              }`}
            >
              <Filter className="h-4 w-4" />
              Filtros
            </button>
          </div>

          {/* Acciones */}
          <div className="flex items-center gap-2">
            <button
              onClick={loadData}
              disabled={isLoading}
              className="p-2 text-gray-500 hover:text-blue-600 hover:bg-gray-100 rounded-lg transition-colors"
              title="Actualizar"
            >
              <RefreshCw className={`h-5 w-5 ${isLoading ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={() => setIsImportExportOpen(true)}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-600 hover:text-blue-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <FileSpreadsheet className="h-4 w-4" />
              Importar / Exportar
            </button>
            <button
              onClick={handleOpenBulkModal}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 border border-blue-200 rounded-lg hover:bg-blue-50 transition-colors"
            >
              <Building2 className="h-4 w-4" />
              Asignar a centro
            </button>
            <button
              onClick={handleCreate}
              className="flex items-center gap-2 px-5 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 shadow-md shadow-blue-100 transition-colors"
            >
              <Plus className="h-4 w-4" />
              Nuevo tipo
            </button>
          </div>
        </div>

        {/* Fila de filtros activos (chips) */}
        {(showInactive || searchQuery) && (
          <div className="flex flex-wrap items-center gap-2 border-t border-gray-100 pt-3">
            <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider mr-2">
              Filtros activos:
            </span>
            
            {showInactive && (
              <div className="inline-flex items-center bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-xs font-medium">
                Estado: Incluye inactivos
                <button 
                  onClick={() => { setShowInactive(false); setPage(1); }}
                  className="ml-1.5 hover:text-blue-900"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            )}
            
            {searchQuery && (
              <div className="inline-flex items-center bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-xs font-medium">
                Búsqueda: "{searchQuery}"
                <button 
                  onClick={() => { setSearchQuery(''); setPage(1); }}
                  className="ml-1.5 hover:text-blue-900"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            )}
            
            <button 
              onClick={() => {
                setShowInactive(false);
                setSearchQuery('');
                setPage(1);
              }}
              className="text-xs text-gray-400 hover:text-red-500 font-medium ml-2 underline underline-offset-2"
            >
              Limpiar todo
            </button>
          </div>
        )}

        {/* Fila inferior: Ordenación y paginación */}
        {showFilters && (
          <div className="flex flex-col sm:flex-row sm:items-center justify-between border-t border-gray-100 pt-3 gap-4">
            {/* Checkbox y ordenación */}
            <div className="flex items-center gap-6">
              <label className="inline-flex items-center cursor-pointer group">
                <input
                  type="checkbox"
                  checked={showInactive}
                  onChange={(e) => {
                    setShowInactive(e.target.checked);
                    setPage(1);
                  }}
                  className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-600 group-hover:text-gray-900 transition-colors">
                  Mostrar inactivos
                </span>
              </label>

              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">Ordenar por:</span>
                <select
                  value={`${sortBy}-${sortOrder}`}
                  onChange={(e) => {
                    const [field, order] = e.target.value.split('-');
                    setSortBy(field as SortField);
                    setSortOrder(order as 'asc' | 'desc');
                    setPage(1);
                  }}
                  className="text-sm border-none bg-transparent py-0 pl-0 pr-8 font-medium text-gray-700 focus:ring-0 cursor-pointer hover:text-blue-600 transition-colors"
                >
                  <option value="name-asc">Nombre (A-Z)</option>
                  <option value="name-desc">Nombre (Z-A)</option>
                  <option value="code-asc">Código (A-Z)</option>
                  <option value="code-desc">Código (Z-A)</option>
                  <option value="createdAt-desc">Fecha (Más reciente)</option>
                  <option value="createdAt-asc">Fecha (Antiguo)</option>
                </select>
              </div>
            </div>

            {/* Items por página */}
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-500">Ver:</span>
              <div className="flex items-center border border-gray-200 rounded-lg overflow-hidden">
                <button
                  onClick={() => { setLimit(10); setPage(1); }}
                  className={`px-3 py-1 text-xs font-semibold border-r border-gray-200 transition-colors ${
                    limit === 10 
                      ? 'bg-blue-50 text-blue-600' 
                      : 'text-gray-600 hover:bg-blue-50 hover:text-blue-600'
                  }`}
                >
                  10
                </button>
                <button
                  onClick={() => { setLimit(25); setPage(1); }}
                  className={`px-3 py-1 text-xs font-semibold border-r border-gray-200 transition-colors ${
                    limit === 25 
                      ? 'bg-blue-50 text-blue-600' 
                      : 'text-gray-600 hover:bg-blue-50 hover:text-blue-600'
                  }`}
                >
                  25
                </button>
                <button
                  onClick={() => { setLimit(50); setPage(1); }}
                  className={`px-3 py-1 text-xs font-semibold transition-colors ${
                    limit === 50 
                      ? 'bg-blue-50 text-blue-600' 
                      : 'text-gray-600 hover:bg-blue-50 hover:text-blue-600'
                  }`}
                >
                  50
                </button>
              </div>
              <span className="text-sm text-gray-500">por página</span>
            </div>
          </div>
        )}
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">{error}</div>}

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {items.length === 0 && !isLoading ? (
          <div className="p-12 text-center">
            <XOctagon className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-1">{searchQuery ? 'Sin resultados' : 'No hay tipos de rechazo'}</h3>
            <p className="text-gray-500">{searchQuery ? 'No se encontraron tipos con ese criterio' : 'Crea tu primer tipo de rechazo'}</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Código</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Nombre</th>
                    <th className="px-6 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Opciones</th>
                    <th className="px-6 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Centros</th>
                    <th className="px-6 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Estado</th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Acciones</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {items.map((item) => (
                    <tr key={item.id} className={`hover:bg-gray-50 ${!item.isActive ? 'bg-gray-50/50' : ''}`}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2.5 py-1 bg-red-50 text-red-700 text-xs font-semibold rounded-lg">{item.code}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`font-medium ${item.isActive ? 'text-gray-900' : 'text-gray-500'}`}>{item.name}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center justify-center gap-2">
                          {item.requiresComment && (
                            <span className="inline-flex items-center px-2 py-0.5 bg-blue-50 text-blue-700 text-xs rounded-full" title="Requiere comentario">
                              <MessageSquare className="h-3 w-3 mr-1" /> Comentario
                            </span>
                          )}
                          {item.isOther && (
                            <span className="inline-flex items-center px-2 py-0.5 bg-gray-100 text-gray-700 text-xs rounded-full" title="Es opción 'Otros'">
                              <MoreHorizontal className="h-3 w-3 mr-1" /> Otros
                            </span>
                          )}
                          {!item.requiresComment && !item.isOther && <span className="text-gray-400">-</span>}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <button
                          onClick={() => handleOpenAssignment(item)}
                          className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-lg border transition-colors ${
                            (item.sitesCount ?? 0) > 0
                              ? 'text-blue-600 border-blue-200 bg-blue-50 hover:bg-blue-100'
                              : 'text-gray-400 border-gray-200 hover:bg-gray-50 hover:text-gray-600'
                          }`}
                          title="Gestionar centros asignados"
                        >
                          <Building2 className="h-3.5 w-3.5" />
                          {(item.sitesCount ?? 0) > 0
                            ? <span className="font-semibold">{item.sitesCount}</span>
                            : <span>—</span>}
                        </button>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <button
  onClick={() => item.code !== 'RECHAZO_TEL' && handleToggleStatus(item)}
  disabled={item.code === 'RECHAZO_TEL'}
  title={item.code === 'RECHAZO_TEL' ? 'No se puede desactivar: se usa automáticamente en el seguimiento de presupuestos' : ''}
  className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full border-2 border-transparent transition-colors ${item.code === 'RECHAZO_TEL' ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'} ${item.isActive ? 'bg-blue-600' : 'bg-gray-300'}`}>
                          <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${item.isActive ? 'translate-x-5' : 'translate-x-0'}`}>
                            <Power className={`h-3 w-3 absolute top-1 left-1 ${item.isActive ? 'text-blue-600' : 'text-gray-400'}`} />
                          </span>
                        </button>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <button onClick={() => handleEdit(item)} className="p-1.5 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded"><Edit2 className="h-4 w-4" /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {meta && meta.totalPages > 1 && (
              <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                <p className="text-sm text-gray-500">Mostrando {(meta.page - 1) * meta.limit + 1} a {Math.min(meta.page * meta.limit, meta.total)} de {meta.total}</p>
                <div className="flex items-center gap-2">
                  <button onClick={() => setPage(meta.page - 1)} disabled={!meta.hasPrevPage || isLoading} className="p-2 rounded border border-gray-300 disabled:opacity-50 hover:bg-gray-50"><ChevronLeft className="h-4 w-4" /></button>
                  <span className="text-sm text-gray-700">Página {meta.page} de {meta.totalPages}</span>
                  <button onClick={() => setPage(meta.page + 1)} disabled={!meta.hasNextPage || isLoading} className="p-2 rounded border border-gray-300 disabled:opacity-50 hover:bg-gray-50"><ChevronRight className="h-4 w-4" /></button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <Modal isOpen={isModalOpen} onClose={handleCloseModal} title={editingItem ? 'Editar tipo de rechazo' : 'Nuevo tipo de rechazo'} subtitle={editingItem ? `Editando: ${editingItem.name}` : 'Completa los datos'}>
        <form onSubmit={handleSubmit(handleSave)}>
          {/* Error banner dentro del modal */}
          {formError && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
              <div className="flex-shrink-0 w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center">
                <AlertCircle className="h-4 w-4 text-red-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-red-800">Error de validación</p>
                <p className="text-sm text-red-600 mt-0.5">{formError}</p>
              </div>
              <button
                type="button"
                onClick={() => setFormError(null)}
                className="flex-shrink-0 p-1 text-red-400 hover:text-red-600 rounded"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Código *</label>
              <input
                type="text"
                maxLength={20}
                {...register('code', { 
                  required: 'El código es obligatorio',
                  maxLength: { value: 20, message: 'Máximo 20 caracteres' }
                })}
                onChange={() => formError && setFormError(null)}
                className={`w-full px-4 py-2.5 border rounded-xl text-sm uppercase ${errors.code || formError ? 'border-red-300 bg-red-50/50' : 'border-gray-200'}`}
                placeholder="REJ001"
              />
              {errors.code && <p className="mt-1 text-xs text-red-500"><AlertCircle className="h-3 w-3 inline mr-1" />{errors.code.message}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nombre *</label>
              <input
                type="text"
                maxLength={100}
                {...register('name', { 
                  required: 'El nombre es obligatorio',
                  maxLength: { value: 100, message: 'Máximo 100 caracteres' }
                })}
                className={`w-full px-4 py-2.5 border rounded-xl text-sm ${errors.name ? 'border-red-300' : 'border-gray-200'}`}
                placeholder="Precio elevado"
              />
              {errors.name && <p className="mt-1 text-xs text-red-500"><AlertCircle className="h-3 w-3 inline mr-1" />{errors.name.message}</p>}
            </div>
            <div className="flex flex-wrap gap-6">
              <label className="flex items-center gap-2">
                <input type="checkbox" {...register('requiresComment')} className="rounded border-gray-300 text-blue-600" />
                <span className="text-sm text-gray-700">Requiere comentario</span>
              </label>
              <label className="flex items-center gap-2">
                <input type="checkbox" {...register('isOther')} className="rounded border-gray-300 text-blue-600" />
                <span className="text-sm text-gray-700">Es opción "Otros"</span>
              </label>
            </div>
          </div>
          <ModalFooter>
            <button type="button" onClick={handleCloseModal} disabled={isSaving} className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 disabled:opacity-50 flex items-center gap-2"><X className="h-4 w-4" />Cancelar</button>
            <button type="submit" disabled={isSaving} className="px-4 py-2.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-xl disabled:opacity-50 flex items-center gap-2">
              {isSaving ? <><Loader2 className="h-4 w-4 animate-spin" />Guardando...</> : <><Save className="h-4 w-4" />{editingItem ? 'Guardar' : 'Crear'}</>}
            </button>
          </ModalFooter>
        </form>
      </Modal>

      {/* ── Modal: Asignación de centros ────────────────────────────────── */}
      {/* ── Modal: Asignación masiva por centro ─────────────────────────── */}
      {bulkModal.isOpen && (
        <Modal
          isOpen={bulkModal.isOpen}
          onClose={() => !bulkModal.isSaving && setBulkModal(prev => ({ ...prev, isOpen: false }))}
          title="Asignar tipos a un centro"
          subtitle="Selecciona un centro y marca los tipos de rechazo disponibles"
        >
          {bulkModal.isLoadingSites ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="h-6 w-6 animate-spin text-gray-300" />
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Centro</label>
                <select
                  value={bulkModal.selectedSiteId}
                  onChange={e => handleBulkSiteChange(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500"
                >
                  {bulkModal.sites.map(s => (
                    <option key={s.id} value={s.id}>{s.name} ({s.code})</option>
                  ))}
                </select>
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-gray-700">
                    Tipos disponibles
                    {bulkModal.selectedTypeIds.length > 0 && (
                      <span className="ml-2 text-xs text-blue-600 font-normal">({bulkModal.selectedTypeIds.length} seleccionados)</span>
                    )}
                  </label>
                  <button onClick={handleBulkSelectAll} className="text-xs text-blue-600 hover:text-blue-800 font-medium">
                    {bulkModal.selectedTypeIds.length === bulkModal.allTypes.length ? 'Deseleccionar todo' : 'Seleccionar todo'}
                  </button>
                </div>
                {bulkModal.isLoadingTypes ? (
                  <div className="flex items-center justify-center py-6">
                    <Loader2 className="h-5 w-5 animate-spin text-gray-300" />
                  </div>
                ) : (
                  <div className="space-y-1.5 max-h-72 overflow-y-auto pr-1">
                    {bulkModal.allTypes.map(type => (
                      <label key={type.id} className="flex items-center gap-3 p-2.5 border border-gray-100 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                        <input
                          type="checkbox"
                          checked={bulkModal.selectedTypeIds.includes(type.id)}
                          onChange={() => handleBulkToggleType(type.id)}
                          className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          <span className="px-1.5 py-0.5 bg-red-50 text-red-700 text-xs font-semibold rounded font-mono">{type.code}</span>
                          <span className="text-sm text-gray-800 truncate">{type.name}</span>
                          {type.requiresComment && <span className="shrink-0 text-xs text-blue-500 font-medium">req. nota</span>}
                        </div>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
          <ModalFooter>
            <button type="button" onClick={() => setBulkModal(prev => ({ ...prev, isOpen: false }))} disabled={bulkModal.isSaving}
              className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 disabled:opacity-50 flex items-center gap-2">
              <X className="h-4 w-4" />Cancelar
            </button>
            <button type="button" onClick={handleBulkSave} disabled={bulkModal.isSaving || bulkModal.isLoadingSites || bulkModal.isLoadingTypes}
              className="px-4 py-2.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-xl disabled:opacity-50 flex items-center gap-2">
              {bulkModal.isSaving ? <><Loader2 className="h-4 w-4 animate-spin" />Guardando...</> : <><Save className="h-4 w-4" />Guardar asignación</>}
            </button>
          </ModalFooter>
        </Modal>
      )}

      {assignmentModal.isOpen && (
        <Modal
          isOpen={assignmentModal.isOpen}
          onClose={() => !assignmentModal.isSaving && setAssignmentModal({ isOpen: false, item: null, sites: [], selectedIds: [], isLoading: false, isSaving: false })}
          title="Centros asignados"
          subtitle={assignmentModal.item ? `"${assignmentModal.item.name}" estará disponible en los centros seleccionados` : ''}
        >
          {assignmentModal.isLoading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="h-6 w-6 animate-spin text-gray-300" />
            </div>
          ) : assignmentModal.sites.length === 0 ? (
            <div className="text-center py-8 text-gray-400 text-sm">No hay centros disponibles</div>
          ) : (
            <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
              {assignmentModal.sites.map(site => (
                <label
                  key={site.id}
                  className="flex items-center gap-3 p-3 border border-gray-100 rounded-xl cursor-pointer hover:bg-gray-50 transition-colors"
                >
                  <input
                    type="checkbox"
                    checked={assignmentModal.selectedIds.includes(site.id)}
                    onChange={() => handleToggleSite(site.id)}
                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <Building2 className="h-4 w-4 text-gray-400 shrink-0" />
                    <span className="font-medium text-gray-800 text-sm truncate">{site.name}</span>
                    <span className="text-xs text-gray-400 font-mono shrink-0">{site.code}</span>
                  </div>
                  {assignmentModal.selectedIds.includes(site.id) && (
                    <span className="shrink-0 text-xs text-blue-600 font-medium">Asignado</span>
                  )}
                </label>
              ))}
            </div>
          )}
          <ModalFooter>
            <button
              type="button"
              onClick={() => setAssignmentModal({ isOpen: false, item: null, sites: [], selectedIds: [], isLoading: false, isSaving: false })}
              disabled={assignmentModal.isSaving}
              className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 disabled:opacity-50 flex items-center gap-2"
            >
              <X className="h-4 w-4" />Cancelar
            </button>
            <button
              type="button"
              onClick={handleSaveAssignment}
              disabled={assignmentModal.isSaving || assignmentModal.isLoading}
              className="px-4 py-2.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-xl disabled:opacity-50 flex items-center gap-2"
            >
              {assignmentModal.isSaving ? <><Loader2 className="h-4 w-4 animate-spin" />Guardando...</> : <><Save className="h-4 w-4" />Guardar</>}
            </button>
          </ModalFooter>
        </Modal>
      )}

      <ImportExportModal
        isOpen={isImportExportOpen}
        onClose={() => setIsImportExportOpen(false)}
        title="Tipos de Rechazo"
        onExport={rejectionTypesImportExport.export}
        onDownloadTemplate={rejectionTypesImportExport.downloadTemplate}
        onPreview={rejectionTypesImportExport.preview}
        onImport={rejectionTypesImportExport.import}
        onImportSuccess={loadData}
        previewColumns={[
          { key: 'code', label: 'Código' },
          { key: 'name', label: 'Nombre' },
          { key: 'requiresComment', label: 'Req. Comentario' },
        ]}
      />

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => !isProcessing && setConfirmDialog({ isOpen: false, item: null, action: 'deactivate' })}
        onConfirm={handleConfirmToggle}
        isLoading={isProcessing}
        variant={confirmDialog.action === 'deactivate' ? 'warning' : 'info'}
        title={confirmDialog.action === 'deactivate' ? '¿Desactivar tipo?' : '¿Activar tipo?'}
        message={confirmDialog.action === 'deactivate' ? `El tipo "${confirmDialog.item?.name}" será desactivado.` : `El tipo "${confirmDialog.item?.name}" será reactivado.`}
        confirmText={confirmDialog.action === 'deactivate' ? 'Desactivar' : 'Activar'}
      />
    </div>
  );
}

export default RejectionTypesAdminPage;
